import { useCallback, useMemo } from "react";
import { useTrackedSessionStorage } from "@/hooks/use-tracked-browser-storage";

// Type definition for our localStorage object
interface ChannelStatusesMap {
  [channelType: string]: string[];
}

/**
 * Hook for managing channel status selection with tab-specific persistence.
 *
 * This hook provides functionality to:
 * 1. Select channel statuses and persist the selection in sessionStorage
 * 2. Maintain tab-specific status selections (view preference)
 * 3. Handle channel switching by maintaining separate selections per channel
 */
type UpdateOptions = {
  intentionalClear?: boolean;
  resetToServerDefaults?: boolean;
};

export function useChannelStatusSelection(channelType?: string) {
  // Manage selected statuses with tab-specific persistence
  const [selectedStatusesMap, setSelectedStatusesMap] =
    useTrackedSessionStorage<ChannelStatusesMap>(
      "channels::explorer::engagement-statuses::filters",
      {},
    );

  // Page-level auto flag
  const [auto, setAuto] = useTrackedSessionStorage<boolean>(
    "channels::explorer::engagement-statuses::auto",
    true,
  );

  // Get the selected statuses for this channel
  const selectedStatuses = useMemo(() => {
    if (!channelType) return [];
    return selectedStatusesMap[channelType] || [];
  }, [channelType, selectedStatusesMap]);

  // Update selected statuses
  const handleStatusesChange = useCallback(
    (statuses: string[], options?: UpdateOptions) => {
      if (!channelType) return;

      // Create a new map to avoid mutating the existing one
      const newMap = { ...selectedStatusesMap };

      // Update logic mirrors campaigns behavior
      if (options?.resetToServerDefaults) {
        delete newMap[channelType];
      } else if (statuses.length === 0) {
        if (options?.intentionalClear) {
          newMap[channelType] = [];
        } else {
          delete newMap[channelType];
        }
      } else {
        newMap[channelType] = statuses;
      }

      setSelectedStatusesMap(newMap);
    },
    [channelType, selectedStatusesMap, setSelectedStatusesMap],
  );

  const clearAllOverrides = useCallback(() => {
    setSelectedStatusesMap({});
  }, [setSelectedStatusesMap]);

  return {
    selectedStatuses,
    handleStatusesChange,
    hasOverride:
      !!channelType && Object.hasOwn(selectedStatusesMap, channelType),
    auto,
    setAuto,
    clearAllOverrides,
    // Expose the map for debugging/testing
    selectedStatusesMap,
  };
}
